<template>
  <div class="container is-fluid event-detail">
    <section class="section">
      <!-- Breadcrumb / back link -->
      <div class="breadcrumb-row">
        <router-link to="/events" class="back-link">
          <span class="icon is-small"><i class="material-icons">arrow_back</i></span>
          Events
        </router-link>
        <a
          v-if="legacyEditUrl"
          :href="legacyEditUrl"
          target="_blank"
          rel="noopener"
          class="legacy-link"
        >
          Open in legacy admin
          <span class="icon is-small"><i class="material-icons is-size-7">open_in_new</i></span>
        </a>
      </div>

      <!-- Header -->
      <div v-if="event" class="event-header">
        <h2 class="title is-3">{{ event.name }}</h2>
        <div class="event-meta">
          <span v-if="event.location">
            <span class="icon is-small"><i class="material-icons is-size-7">place</i></span>
            {{ event.location }}
          </span>
          <span v-if="event.start_date">
            <span class="icon is-small"><i class="material-icons is-size-7">event</i></span>
            {{ formatRange(event.start_date, event.end_date) }}
          </span>
          <span v-if="event.is_published" class="tag is-success is-light">Published</span>
          <span v-else class="tag is-light">Draft</span>
        </div>
      </div>
      <div v-else-if="loading" class="has-text-grey py-4">Loading event…</div>
      <div v-else class="has-text-grey py-4">
        Event <strong>#{{ eventId }}</strong> not found in loaded data.
        <a @click="refreshEvents">Retry</a>
      </div>

      <!-- In Use summary + Receive action -->
      <div class="in-use-row mt-3">
        <router-link
          :to="{ path: '/devices', query: { event: eventName, status: 'In Use' } }"
          class="status-card in-use-card"
        >
          <div class="status-card-head">
            <span class="status-dot dot-inuse"></span>
            <span class="status-label">In Use</span>
          </div>
          <div class="status-count">{{ inUseCard.total }}</div>
          <div class="status-breakdown">{{ inUseCard.breakdown }}</div>
        </router-link>
        <div class="in-use-actions">
          <button
            class="button is-success"
            :disabled="inUseCard.total === 0"
            @click="openReceive"
          >
            <span class="icon is-small"><i class="material-icons">input</i></span>
            <span>Receive {{ inUseCard.total }} devices</span>
          </button>
          <p class="action-hint">
            Moves all In Use devices in this event back to a base and clears their event assignment.
          </p>
        </div>
      </div>

      <!-- Category → Model breakdown for this event -->
      <div class="section-header mt-4">
        <h3 class="section-title">Devices in this event</h3>
        <router-link
          :to="{ path: '/devices', query: { event: eventName } }"
          class="is-size-7"
        >
          Open full devices list →
        </router-link>
      </div>

      <div class="box breakdown-box">
        <div v-if="eventDevices.length === 0" class="has-text-grey py-4 has-text-centered">
          No devices assigned to this event.
        </div>
        <div
          v-for="cat in categoryBreakdown"
          :key="cat.name"
          class="breakdown-category"
        >
          <div class="category-row">
            <span class="category-name">{{ cat.name }}</span>
            <span class="category-total">{{ cat.total }} total</span>
          </div>
          <div class="category-models">
            <div
              v-for="model in cat.models"
              :key="cat.name + '::' + model.name"
              class="model-row"
            >
              <router-link
                class="model-name"
                :to="{ path: '/devices', query: { event: eventName, category: cat.name, model: model.name } }"
              >
                <span class="model-branch">└─</span>
                <span class="model-label">{{ model.name }}</span>
                <span class="model-count">{{ model.total }}</span>
              </router-link>
              <div class="model-chips">
                <router-link
                  v-for="chip in model.statusChips"
                  :key="chip.status"
                  class="chip"
                  :to="{ path: '/devices', query: { event: eventName, category: cat.name, model: model.name, status: chip.status } }"
                >
                  <span class="status-dot" :class="`dot-${chip.key}`"></span>
                  {{ chip.status }}
                  <span class="chip-count">{{ chip.count }}</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Modal v-if="receiveModal.isActive" :config="receiveModal">
      <template v-slot:header>
        <h4 class="title is-4">Receive devices from event</h4>
      </template>
      <p class="mb-3 has-text-grey">
        {{ inUseCard.total }} In Use device(s) from <strong>{{ eventName }}</strong> will be returned.
      </p>
      <Select :config="selectBase" v-model="receiveBase" />
      <template v-slot:footer>
        <button
          class="button is-success"
          :disabled="!receiveBase || inUseCard.total === 0"
          @click="submitReceive"
        >
          Receive devices ({{ inUseCard.total }})
        </button>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts">
import { ISelect } from '@/components/form';
import Select from '@/components/form/select.vue';
import Modal from '@/components/modal.vue';
import { IModal } from '@/components';
import store from '@/store';
import { IBulkDevicesBase, IBulkDevicesEvent, IStateDevice } from '@/store/modules/devices';
import { IStateEvent } from '@/store/modules/events';
import formatDate from '@/utils/formatDate';
import { Component, Vue } from 'vue-property-decorator';
import { MutationPayload } from 'vuex';
import { Action, Getter } from 'vuex-class';

interface IStatusChip { status: string; key: string; count: number; }
interface IModelBreakdown { name: string; total: number; statusChips: IStatusChip[]; }
interface ICategoryBreakdown { name: string; total: number; models: IModelBreakdown[]; }

const STATUS_DEFS: Array<{ status: string; key: string }> = [
  { status: 'In Use', key: 'inuse' },
  { status: 'Available', key: 'available' },
  { status: 'Repairing', key: 'repairing' },
  { status: 'Receiving', key: 'receiving' },
  { status: 'Missing', key: 'missing' },
];

const formatCategoryBreakdown = (counts: Record<string, number>): string => {
  const entries = Object.entries(counts).filter(([, v]) => v > 0);
  if (entries.length === 0) return '0 devices';
  entries.sort((a, b) => {
    if (a[0] === 'BEAMER') return -1;
    if (b[0] === 'BEAMER') return 1;
    return b[1] - a[1];
  });
  return entries.map(([cat, count]) => `${count} ${cat}`).join(' · ');
};

@Component({ name: 'EventDetail', components: { Modal, Select } })
class EventDetail extends Vue {
  @Getter('getEvents') public events!: IStateEvent[];
  @Getter('getDevices') public devices!: IStateDevice[];
  @Getter('getEventsState') public eventsState!: { loading: boolean };
  @Action('fetchEvents') public fetchEvents!: (params?: { page?: number; pageSize?: number }) => void;
  @Action('fetchDevices') public fetchDevices!: (params?: { page?: number; pageSize?: number }) => void;
  @Action('assignDevices') public assignDevices!: (payload: IBulkDevicesEvent) => void;
  @Action('changeDevicesLocation') public changeDevicesLocation!: (payload: IBulkDevicesBase) => void;

  public receiveBase = '';
  public receiveModal: IModal = {
    isActive: false,
    close: () => {
      this.receiveModal = { ...this.receiveModal, isActive: false };
      this.receiveBase = '';
    },
  };
  public selectBase: ISelect = {
    id: 'event-receive-base',
    default: 'Select base',
    value: '',
    options: [
      { value: 'anadia', label: 'Anadia' },
      { value: 'oporto', label: 'Porto' },
      { value: 'lisbon', label: 'Lisbon' },
      { value: 'visionvolt', label: 'Vision volt' },
    ],
  };

  public get eventId(): number {
    return parseInt(this.$route.params.id, 10);
  }

  public get event(): IStateEvent | undefined {
    return this.events.find((e) => e.id === this.eventId);
  }

  public get eventName(): string {
    return this.event ? this.event.name : '';
  }

  public get loading(): boolean {
    return this.eventsState.loading && this.events.length === 0;
  }

  public get legacyEditUrl(): string {
    const base = process.env.VUE_APP_REDIRECT_URL;
    if (!base || !this.eventId) return '';
    return `${base}events/${this.eventId}`;
  }

  public get eventDevices(): IStateDevice[] {
    if (!this.eventName) return [];
    const byName = this.eventName.toLowerCase();
    return this.devices.filter((d) => {
      if (d.current_event && d.current_event.id === this.eventId) return true;
      return (d.current_event_name || '').toLowerCase() === byName;
    });
  }

  public get inUseDevices(): IStateDevice[] {
    return this.eventDevices.filter(
      (d) => (d.status || '').toLowerCase() === 'in use',
    );
  }

  public get inUseCard() {
    const list = this.inUseDevices;
    const catCounts: Record<string, number> = {};
    list.forEach((d) => {
      const cat = d.category_name || 'UNCATEGORIZED';
      catCounts[cat] = (catCounts[cat] || 0) + 1;
    });
    return {
      total: list.length,
      breakdown: formatCategoryBreakdown(catCounts),
    };
  }

  public get categoryBreakdown(): ICategoryBreakdown[] {
    const byCat: Record<string, Record<string, IStateDevice[]>> = {};
    this.eventDevices.forEach((d) => {
      const cat = d.category_name || 'UNCATEGORIZED';
      const model = d.model_name || 'UNSPECIFIED';
      if (!byCat[cat]) byCat[cat] = {};
      if (!byCat[cat][model]) byCat[cat][model] = [];
      byCat[cat][model].push(d);
    });
    return Object.keys(byCat)
      .map((catName) => {
        const models = Object.keys(byCat[catName])
          .map((modelName) => {
            const modelDevices = byCat[catName][modelName];
            const statusChips = STATUS_DEFS.map((def) => ({
              status: def.status,
              key: def.key,
              count: modelDevices.filter(
                (d) => (d.status || '').toLowerCase() === def.status.toLowerCase(),
              ).length,
            })).filter((c) => c.count > 0);
            return { name: modelName, total: modelDevices.length, statusChips };
          })
          .sort((a, b) => b.total - a.total);
        return {
          name: catName,
          total: models.reduce((sum, m) => sum + m.total, 0),
          models,
        };
      })
      .sort((a, b) => b.total - a.total);
  }

  public created() {
    // Ensure we have both events and devices loaded for aggregation.
    if (this.events.length === 0) {
      this.fetchEvents({ page: 1, pageSize: 100 });
    }
    if (this.devices.length === 0) {
      this.fetchDevices({ page: 1, pageSize: 1000 });
    }
  }

  public refreshEvents() {
    this.fetchEvents({ page: 1, pageSize: 100 });
  }

  public openReceive() {
    if (this.inUseCard.total === 0) return;
    this.receiveBase = '';
    this.receiveModal = { ...this.receiveModal, isActive: true };
  }

  public submitReceive() {
    if (!this.receiveBase) return;
    const ids = this.inUseDevices
      .map((d) => d.id)
      .filter((id): id is number => typeof id === 'number');
    if (ids.length === 0) return;

    this.assignDevices({ ids, current_event_id: null });

    const unsubscribe = store.subscribe((mutation: MutationPayload) => {
      if (mutation.type === 'setDevicesEvent') {
        unsubscribe();
        this.changeDevicesLocation({ ids, base: this.receiveBase });
      }
    });

    this.receiveModal = { ...this.receiveModal, isActive: false };
    this.receiveBase = '';
  }

  public formatRange(start: string, end: string): string {
    if (!start) return '';
    const s = formatDate(start);
    const e = end ? formatDate(end) : '';
    return e ? `${s} → ${e}` : s;
  }
}

export default EventDetail;
</script>

<style scoped lang="scss">
$green: #3fb982;
$amber: #d9a14a;
$red: #d66060;
$blue: #4a90d6;
$grey-faint: #8a8a8a;

$surface: #1b2a44;
$surface-alt: #223250;
$border: rgba(255, 255, 255, 0.08);
$border-strong: rgba(255, 255, 255, 0.14);
$text: #e5e9f2;
$text-muted: #9aa6bf;
$text-faint: #6d7890;
$accent: #8fb4ff;

.event-detail ::v-deep .title,
.event-detail ::v-deep .subtitle { color: $text; }

.breadcrumb-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: $accent;
  font-size: 0.85rem;
  &:hover { text-decoration: underline; }
}
.legacy-link {
  font-size: 0.78rem;
  color: $text-muted;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  &:hover { color: $accent; }
}

.event-header .title { margin-bottom: 0.35rem; color: $text; }
.event-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: 0.85rem;
  color: $text-muted;
  flex-wrap: wrap;
  .icon { vertical-align: middle; }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
}
.section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $text-muted;
  margin: 0;
}

.status-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 0.35rem;
  vertical-align: middle;
}
.dot-inuse { background: $blue; }
.dot-available { background: $green; }
.dot-repairing { background: $amber; }
.dot-receiving { background: $amber; }
.dot-missing { background: $red; }
.dot-other { background: $grey-faint; }

.in-use-row {
  display: flex;
  gap: 1rem;
  align-items: stretch;
  flex-wrap: wrap;
}
.in-use-card {
  min-width: 260px;
  flex: 0 0 auto;
}
.in-use-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
  min-width: 260px;
  .button { align-self: flex-start; }
  .action-hint {
    font-size: 0.78rem;
    color: $text-faint;
    max-width: 380px;
    margin: 0;
  }
}

.status-card {
  display: block;
  background: $surface;
  border: 1px solid $border;
  border-radius: 6px;
  padding: 1.1rem;
  color: $text;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover {
    border-color: $border-strong;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
  }
  &.is-alert { border-color: rgba(214, 96, 96, 0.55); }
}
.status-card-head {
  display: flex; align-items: center; gap: 0.4rem;
  margin-bottom: 0.5rem;
}
.status-label { font-size: 0.8rem; color: $text-muted; }
.status-count {
  font-size: 2rem;
  font-weight: 300;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 0.4rem;
  color: $text;
}
.status-breakdown { font-size: 0.72rem; color: $text-faint; }

.breakdown-box {
  padding: 0;
  background: $surface;
  border: 1px solid $border;
  color: $text;
  box-shadow: none;
}
.breakdown-category + .breakdown-category { border-top: 1px solid $border; }
.category-row {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
}
.category-name { font-weight: 600; color: $text; }
.category-total {
  margin-left: auto;
  color: $text-muted;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}
.category-models { padding: 0 1rem 0.6rem 2rem; }
.model-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-top: 1px solid $border;
  flex-wrap: wrap;
}
.model-name {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: $text;
  &:hover { color: $accent; }
}
.model-branch { color: $text-faint; font-size: 0.75rem; }
.model-label { font-weight: 500; }
.model-count { color: $text-muted; font-size: 0.78rem; font-variant-numeric: tabular-nums; }
.model-chips {
  display: flex; gap: 0.4rem;
  flex-wrap: wrap;
  margin-left: auto;
}
.chip {
  display: inline-flex; align-items: center; gap: 0.3rem;
  background: $surface-alt;
  border: 1px solid $border;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.72rem;
  color: $text-muted;
  &:hover { border-color: $accent; color: #cdd9f3; }
}
.chip-count { font-variant-numeric: tabular-nums; font-weight: 600; margin-left: 0.15rem; color: $text; }
</style>
